// Procedural surface patches for the true-scale local scene.
//
// A patch is four nested curved grids (core / inner / mid / outer) built in
// the tangent frame of a site on a body: X east, Y up, Z south. Heights
// come from `terrainHeight`, which is also what the physics uses for
// ground contact, so what you see is what you land on. The outer ring
// alpha-fades at its rim so it dissolves into the scaled-scene planet
// the app draws behind it.
//
// On Earth the ground is real: NAIP aerial imagery around the Cape, the Blue
// Marble region tile beyond it and the planet's day map beyond that, with a
// land/water distance field (coast.js) deciding per pixel between ground and
// sea. The terrain is at 0 m over water and a low coastal plain of a few metres
// on land, with a beach and dune line along the Atlantic, as Merritt Island is.
// Water shading lives in water.js. The Moon and Mars keep procedural relief.

import * as THREE from 'three';
import { runwayFlats } from './runway.js';
import { earthImageryUniforms, EARTH_IMAGERY_GLSL } from './earthimagery.js';
import { makeNightPatchMaterial, nightPatchBeforeRender, nightSunUniforms } from './nightimagery.js';
import { EARTH_OCEAN_GLSL } from '../shaders/earthocean.js';
import { craterHeight } from './craters.js';
import { moonDemHeight, moonAlbedo, onMoonDem, loadMoonBase } from './moondem.js';
import { moonSpan } from '../data/moonImagerySource.js';
import { makeMoonMaterial, setMoonPatchFrame } from './moonmaterial.js';
import { createHorizonMaps, horizonMapSteps } from './moonshadow.js';
import { getTexture, TEXTURE_BASE } from './textures.js';
import { coastDistance, coastUniforms, coastLevels, loadCoast, onCoastReady, coastReady, globalOceanAt } from './coast.js';
import { WATER_PARS, WATER_COLOURS, waterUniforms, seaUniforms, updateWaves } from './water.js';
import { AERIAL } from './aerial.js';
import {
  setDayTexture, dayMapSize, farHeight, farWater, inCapeRegion, coastLatticeOffsets,
  FAR_PARS, FAR_MAP_PARS, FAR_FRAME, FAR_COAST, FAR_LAND
} from './earthfar.js';

// Real imagery around KSC (NASA Blue Marble NG via GIBS, 500 m/px), built by
// scripts/fetch-florida-tile.mjs. Bounds in degrees, must match that script.
export const EARTH_REGION = { lonMin: -92.25, lonMax: -72.0, latMin: 18.0, latMax: 38.25 };
let _regionTex = null;
function regionTexture() {
  if (!_regionTex) {
    _regionTex = new THREE.TextureLoader().load(TEXTURE_BASE + 'florida_region.webp');
    _regionTex.colorSpace = THREE.SRGBColorSpace;
    _regionTex.wrapS = _regionTex.wrapT = THREE.ClampToEdgeWrapping;
    _regionTex.anisotropy = 16;
  }
  return _regionTex;
}

const TWO_PI = Math.PI * 2;
const DEG = 180 / Math.PI;

// Shared by every Earth terrain material: the camera's altitude, set once a
// frame by updateTerrainWater. Only one Earth patch is ever on screen.
export const earthViewUniforms = {
  uCamAlt: { value: 0 },
  // 1 once the imagery stream's water mask is the app's ocean texture
  // (earthocean.js), which the far sea reads for depth and Kd490.
  uEarthOceanOn: { value: 0 }
};

// --- Height field --------------------------------------------------------------

function hash3(x, y, z) {
  let h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453123;
  return h - Math.floor(h);
}
function smooth(t) { return t * t * (3 - 2 * t); }
// Value noise on a 3D lattice (input in lattice units).
function noise3(x, y, z) {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  const fx = smooth(x - ix), fy = smooth(y - iy), fz = smooth(z - iz);
  const lerp = (a, b, t) => a + (b - a) * t;
  const c000 = hash3(ix, iy, iz), c100 = hash3(ix + 1, iy, iz);
  const c010 = hash3(ix, iy + 1, iz), c110 = hash3(ix + 1, iy + 1, iz);
  const c001 = hash3(ix, iy, iz + 1), c101 = hash3(ix + 1, iy, iz + 1);
  const c011 = hash3(ix, iy + 1, iz + 1), c111 = hash3(ix + 1, iy + 1, iz + 1);
  return lerp(
    lerp(lerp(c000, c100, fx), lerp(c010, c110, fx), fy),
    lerp(lerp(c001, c101, fx), lerp(c011, c111, fx), fy),
    fz
  ) * 2 - 1;
}

// Earth's octaves are a Florida coastal plain: a metre or two of relief over
// kilometres. The +-80 m hills this used to have stood where the Banana River
// is. Where there is no coastline data (headless tests) Earth is all land.
export const TERRAIN_PROFILES = {
  earth: { octaves: [[30e3, 1.2], [7e3, 0.7], [1.5e3, 0.25]], base: 3.0 },
  // The Moon's large-scale relief is LOLA's (moondem.js); these octaves are
  // only a gentle roll under the finest height posts (167 m). Smaller relief
  // is the crater field: value noise on a lattice reads as a grid of blocks
  // under a low sun.
  moon: { octaves: [[700, 4], [260, 1.5]], base: 0 },
  mars: { octaves: [[50e3, 300], [15e3, 120], [4e3, 40], [900, 10]], base: 0 }
};

// Flattened launch sites: { lat, lon (deg), radius (m), height (m) }. Earth's
// is LC-39A's perimeter road, 470 m out; the pad itself is site geometry.
export const SITES = {
  earth: [{ id: 'ksc', name: 'Kennedy Space Center', lat: 28.6083, lon: -80.6041, radius: 470, height: 3, fade: 250 }]
};

// Ground levelled for buildings away from the launch site, terrain only
// (physics treats launch SITES specially; these are just flat ground). The
// VAB, the Launch Control Center and the campus south of them, and the
// tracking antennas on the old park site north-east of the VAB.
export const FLATS = {
  earth: [
    { lat: 28.5840, lon: -80.6495, radius: 620, height: 3, fade: 220 },
    { lat: 28.5925, lon: -80.6458, radius: 260, height: 3, fade: 120 },
    // The Shuttle Landing Facility's runway and the Spaceplane Hangar apron.
    ...runwayFlats()
  ]
};

const _cd = { d: 0, ocean: 0 };

// Land height (m) given the plain's height and the coastline distances. The
// lagoon and marsh shores fall to the waterline over 45 m. The Atlantic side
// rises from the swash to a 2 m berm and a dune line about 95 m inland that
// peaks near 5 m, then settles back to the plain.
function coastalHeight(plain, c) {
  if (c.d <= 0) return 0;
  let h = plain;
  if (c.ocean < 330) {
    const dO = Math.max(0, c.ocean);
    const beach = 2.0 * smooth(Math.min(1, dO / 45)) + 2.6 * Math.exp(-(((dO - 95) / 40) ** 2));
    const t = smooth(Math.min(1, Math.max(0, (dO - 130) / 200)));
    h = beach + (plain - beach) * t;
  }
  // Inland water closer than the sea: ramp down to it.
  if (c.d < c.ocean - 1) h *= smooth(Math.min(1, c.d / 45));
  return h;
}

// Surface age (Gyr) at a unit direction on the Moon, from the WAC albedo:
// dark maria about 3.5 Gyr, bright highlands saturated (4.2), and the
// brightest ground (fresh ejecta and rays) young. Null before the map loads.
export function moonAgeAt(dx, dy, dz) {
  const a = moonAlbedo(Math.asin(Math.max(-1, Math.min(1, dy))), Math.atan2(-dz, dx));
  if (a == null) return null;
  if (a > 0.2) return 4.2 - 3.0 * Math.min(1, (a - 0.2) / 0.08);
  return 3.4 + 0.8 * smooth(Math.max(0, Math.min(1, (a - 0.08) / 0.05)));
}

// Height above the reference sphere (metres) at geodetic lat/lon (radians).
// minFeature (m) drops relief finer than a mesh can draw (crater classes
// and noise octaves below it); physics asks for full detail.
export function terrainHeight(bodyKey, lat, lon, minFeature = 0) {
  const prof = TERRAIN_PROFILES[bodyKey];
  if (!prof) return 0;
  const cl = Math.cos(lat);
  const dx = cl * Math.cos(lon), dy = Math.sin(lat), dz = -cl * Math.sin(lon);
  const R = bodyKey === 'earth' ? 6371000 : bodyKey === 'moon' ? 1737400 : 3389500;
  let h = prof.base;
  for (const [wavelength, amp] of prof.octaves) {
    if (wavelength < minFeature * 0.5) continue;
    const f = R / wavelength;
    h += noise3(dx * f + 17.3, dy * f + 5.1, dz * f + 9.7) * amp;
  }
  // The Moon: real heights, and craters smaller than the data resolves
  // (craters.js), counted by the local terrain age.
  if (bodyKey === 'moon') {
    // Mesh builds ask for the tiles they draw; physics reads what is loaded.
    h += moonDemHeight(lat, lon, minFeature, minFeature > 0).h;
    // A bowl needs six grid cells to come out round (at two and a half it
    // rasterises into crosses and bars), and physics matches the mesh:
    // craters under 30 m are drawn per pixel by the terrain shader only.
    h += craterHeight(dx, dy, dz, R, Math.max(minFeature * 2.4, 30), moonAgeAt);
  }
  // Level the launch site and building plots first, then let the coast cut
  // in, so water inside a levelled area still lies at sea level.
  const flats = FLATS[bodyKey];
  const sites = SITES[bodyKey];
  for (const list of [flats, sites]) {
    if (!list) continue;
    for (const s of list) {
      const sl = s.lat * Math.PI / 180, so = s.lon * Math.PI / 180;
      // great-circle distance
      const cosD = Math.sin(sl) * dy + Math.cos(sl) * cl * Math.cos(lon - so);
      const d = Math.acos(Math.min(1, Math.max(-1, cosD))) * R;
      if (d < s.radius + s.fade) {
        const t = d < s.radius ? 0 : (d - s.radius) / s.fade;
        h = h * smooth(t) + s.height * (1 - smooth(t));
      }
    }
  }
  if (bodyKey === 'earth') {
    const latD = lat * DEG, lonD = lon * DEG;
    // Inside the Cape's box the coastline data shapes the ground exactly as
    // it always has. Outside it there is only the day map, and earthfar.js
    // turns that into a shoreline and a few tens of metres of relief.
    if (inCapeRegion(latD, lonD)) {
      const c = coastDistance(latD, lonD, _cd);
      if (c) h = coastalHeight(h, c);
    } else {
      h = farHeight(h, latD, lonD, dx, dy, dz, minFeature);
    }
  }
  return h;
}

// Render-only bathymetry: how far the drawn ground sits below sea level over
// water. Physics keeps terrainHeight at 0 there, and so does everything that
// stands on the ground; this is only the mesh. Two reasons for it. The sea
// surface (sea.js) needs room under it for its troughs, and a ring that
// interpolates a 1.2 km cell across a shoreline otherwise draws the water
// itself a metre or two ABOVE sea level and hides that surface entirely. And
// the shallows need a bed for the light to come back off. The profile is the
// one the sea shades with: Dean's equilibrium beach with a longshore bar for
// the Atlantic, a shallow shelf for the lagoons, capped so nothing visible
// from altitude moves by more than a couple of metres.
const SEABED_CAP = 2.6;
export function seabedDrop(c) {
  if (!c || c.d >= 0) return 0;
  const sea = 1 - smooth(Math.min(1, Math.max(0, (c.ocean + 40) / 80)));
  const deep = 0.11 * Math.pow(Math.max(0, -c.ocean), 2 / 3);
  const shelf = 0.14 * Math.pow(Math.max(0, -c.d), 0.55);
  // Held at the waterline itself over the first tens of metres: a ring too
  // coarse to draw the ramp puts its first wet vertex somewhere inside that
  // band, and a bed that already stepped down by then shows as a wall.
  const lip = smooth(Math.min(1, -c.d / 30));
  return Math.min(SEABED_CAP, (deep * sea + shelf * (1 - sea)) * lip);
}

// True over sea, lagoon, river or lake at geodetic lat/lon (radians), for
// splashdowns. Uses the coastline levels near the Cape, the day map's own
// shoreline elsewhere (earthfar.js, the same one the mesh is built on) and
// the app's global ocean mask where neither has loaded; false off Earth.
export function isWater(bodyKey, lat, lon) {
  if (bodyKey !== 'earth') return false;
  const latD = lat * DEG, lonD = lon * DEG;
  if (inCapeRegion(latD, lonD)) {
    const c = coastDistance(latD, lonD, _cd);
    if (c) return c.d < 0;
  } else {
    const far = farWater(latD, lonD);
    if (far !== null) return far;
  }
  return globalOceanAt(latD, lonD) === true;
}

// --- Patch geometry -------------------------------------------------------------

export function siteBasis(lat, lon) {
  const cl = Math.cos(lat);
  const up = new THREE.Vector3(cl * Math.cos(lon), Math.sin(lat), -cl * Math.sin(lon));
  let east = new THREE.Vector3(0, 1, 0).cross(up);
  if (east.lengthSq() < 1e-9) east.set(1, 0, 0);
  east.normalize();
  const north = new THREE.Vector3().crossVectors(up, east).normalize();
  return { up, east, north };
}

// Direction on the unit sphere at local tangent offset (x east, z south) metres.
export function dirAt(basis, R, x, z, out) {
  const gx = x, gn = -z;
  const d = Math.sqrt(gx * gx + gn * gn);
  const a = d / R;
  if (d < 1e-6) return out.copy(basis.up);
  const ca = Math.cos(a), sa = Math.sin(a);
  out.copy(basis.up).multiplyScalar(ca);
  out.x += (basis.east.x * gx + basis.north.x * gn) / d * sa;
  out.y += (basis.east.y * gx + basis.north.y * gn) / d * sa;
  out.z += (basis.east.z * gx + basis.north.z * gn) / d * sa;
  return out;
}

// What the mesh draws: the physics height, less the seabed over water. Both
// ring builders go through this, so a finer ring still morphs onto its
// coarser neighbour exactly.
const _cdR = { d: 0, ocean: 0 };
function renderHeight(bodyKey, lat, lon, minFeature = 0) {
  const h = terrainHeight(bodyKey, lat, lon, minFeature);
  if (bodyKey !== 'earth') return h;
  const c = coastDistance(lat * DEG, lon * DEG, _cdR);
  return c ? h - seabedDrop(c) : h;
}

// Height sampled the way the COARSER ring's mesh interpolates it (bilinear
// on that ring's grid), so a finer ring can morph its rim onto it and the
// two meet without cracks or skirts.
// Relief a ring with this cell size (m) can draw: features under about two
// and a half cells alias rather than show.
const lodFeature = (cell) => cell * 2.5;

function coarseHeight(bodyKey, R, basis, x, z, coarseHalf, coarseCells) {
  const spacing = (2 * coarseHalf) / coarseCells;
  const gx = (x + coarseHalf) / spacing, gz = (z + coarseHalf) / spacing;
  const i0 = Math.floor(gx), j0 = Math.floor(gz);
  const fx = gx - i0, fz = gz - j0;
  const dir = new THREE.Vector3();
  const minF = lodFeature(spacing);
  const h = (i, j) => {
    dirAt(basis, R, -coarseHalf + i * spacing, -coarseHalf + j * spacing, dir);
    return renderHeight(bodyKey, Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1)), Math.atan2(-dir.z, dir.x), minF);
  };
  const a = h(i0, j0), b = h(i0 + 1, j0), c = h(i0, j0 + 1), d = h(i0 + 1, j0 + 1);
  return (a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz;
}

// `geo` carries each vertex's longitude and latitude in degrees relative to
// the patch centre: small numbers keep full float precision, and the shader
// turns them into texture coordinates for each imagery level exactly.
function buildRing(bodyKey, R, basis, centreH, halfSize, cells, hole, opts, lat0Deg, lon0Deg) {
  const out = {};
  const steps = buildRingSteps(bodyKey, R, basis, centreH, halfSize, cells, hole, opts, lat0Deg, lon0Deg, out);
  while (!steps.next().done) { /* run to completion */ }
  return out.geometry;
}

// The same build, one row of vertices per step, so a patch can move to a new
// centre a few milliseconds a frame (TerrainPatch.recentre / tick).
function* buildRingSteps(bodyKey, R, basis, centreH, halfSize, cells, hole, opts, lat0Deg, lon0Deg, out) {
  const n = cells + 1;
  const positions = new Float32Array(n * n * 3);
  const uvs = new Float32Array(n * n * 2);
  const geos = new Float32Array(n * n * 2);
  const rims = new Float32Array(n * n);
  const indices = [];
  const dir = new THREE.Vector3();
  const p = new THREE.Vector3();
  const centre = basis.up.clone().multiplyScalar(R + centreH);
  const morph = opts.morph || null;   // { half, cells } of the coarser ring outside this one
  const band = opts.morphBand || 0.15;
  const minF = lodFeature((2 * halfSize) / cells);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const x = -halfSize + (2 * halfSize * i) / cells;
      const z = -halfSize + (2 * halfSize * j) / cells;
      dirAt(basis, R, x, z, dir);
      const lat = Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1));
      const lon = Math.atan2(-dir.z, dir.x);
      let h = renderHeight(bodyKey, lat, lon, minF);
      if (morph) {
        const rr = Math.max(Math.abs(x), Math.abs(z)) / halfSize;
        const t = THREE.MathUtils.clamp((rr - (1 - band)) / band, 0, 1);
        if (t > 0) h = h * (1 - t) + coarseHeight(bodyKey, R, basis, x, z, morph.half, morph.cells) * t;
      }
      p.copy(dir).multiplyScalar(R + h).sub(centre);
      // to local frame
      const k = (j * n + i);
      positions[k * 3] = p.dot(basis.east);
      positions[k * 3 + 1] = p.dot(basis.up);
      positions[k * 3 + 2] = -p.dot(basis.north);
      uvs[k * 2] = THREE.MathUtils.euclideanModulo(lon / TWO_PI + 0.5, 1);
      uvs[k * 2 + 1] = 0.5 + lat / Math.PI;
      geos[k * 2] = THREE.MathUtils.euclideanModulo(lon * DEG - lon0Deg + 180, 360) - 180;
      geos[k * 2 + 1] = lat * DEG - lat0Deg;
      const rr = Math.max(Math.abs(x), Math.abs(z)) / halfSize;
      rims[k] = opts.rimFade ? 1 - smooth(THREE.MathUtils.clamp((rr - 0.6) / 0.4, 0, 1)) : 1;
    }
    yield;
  }
  const holeCells = hole ? Math.round((hole / (2 * halfSize)) * cells) : 0;
  const lo = Math.floor(cells / 2 - holeCells / 2), hi = Math.ceil(cells / 2 + holeCells / 2);
  for (let j = 0; j < cells; j++) {
    for (let i = 0; i < cells; i++) {
      if (holeCells > 0 && i >= lo && i < hi && j >= lo && j < hi) continue;
      const a = j * n + i, b = a + 1, c = a + n, d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  g.setAttribute('geo', new THREE.BufferAttribute(geos, 2));
  g.setAttribute('rim', new THREE.BufferAttribute(rims, 1));
  g.setIndex(indices);
  g.computeVertexNormals();
  out.geometry = g;
}

const DETAIL_GLSL = `
float tHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float tNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(tHash(i), tHash(i + vec2(1.0, 0.0)), f.x), mix(tHash(i + vec2(0.0, 1.0)), tHash(i + vec2(1.0, 1.0)), f.x), f.y);
}
`;

// --- Earth material --------------------------------------------------------------

const EARTH_VERT_PARS = /* glsl */`
attribute float rim;
attribute vec2 geo;
varying float vRim;
varying vec3 vLocalPos;
varying vec2 vGeo;
varying vec3 vWaveX;
varying vec3 vWaveZ;
`;

const EARTH_VERT_MAIN = /* glsl */`
vRim = rim;
vLocalPos = position;
vGeo = geo;
// View-space east and south of the patch frame, for the wave normals.
vWaveX = normalize(normalMatrix * vec3(1.0, 0.0, 0.0));
vWaveZ = normalize(normalMatrix * vec3(0.0, 0.0, 1.0));
`;

const EARTH_FRAG_PARS = /* glsl */`
varying float vRim;
varying vec3 vLocalPos;
varying vec2 vGeo;
varying vec3 vWaveX;
varying vec3 vWaveZ;
uniform float uCoastOn;
uniform highp sampler2DArray uCoastMasks;
uniform sampler2D uCoastNearImg;
uniform sampler2D uCoastFarImg;
uniform sampler2D uRegionImg;
uniform float uCoastNearImgOn;
uniform float uCoastFarImgOn;
uniform vec3 uCoastRange;
uniform vec3 uCoastBorder;
uniform vec4 uXfNear;
uniform vec4 uXfFar;
uniform vec4 uXfRegion;
uniform float uSite;
uniform float uDebug;
uniform sampler2D uDetailA;
uniform sampler2D uDetailB;
uniform sampler2D uDetailC;
uniform sampler2D uDetailD;
uniform float uDetailScale;
uniform float uCamAlt;
uniform float uEarthOceanOn;
uniform vec2 uImageryOrigin;
${EARTH_IMAGERY_GLSL}
${EARTH_OCEAN_GLSL}
${DETAIL_GLSL}
${WATER_PARS}
${WATER_COLOURS}
// The sky dome's haze over the ground seen from above the air (sky.js), in
// closed form. The dome is drawn before the terrain and composites over the
// scaled Earth, so wherever the patch covers that Earth it covers the dome's
// haze too; the far sea, which has to meet the Earth round it, lays the same
// layer back over itself. Single scattering through a plane-parallel air
// with the dome's own Earth constants (Rayleigh, Mie and the ozone tent,
// sun intensity 10, g 0.7, 0.05 isotropic Rayleigh), integrated in eight
// steps of the Rayleigh column, and the dome's ground-haze share (0.55 well
// inside the disc from above the air, the full column toward the limb).
// Returns the premultiplied colour and alpha the dome would have drawn.
vec4 rkDomeGroundHaze(float muV, float muS, float cosTheta, float camAlt) {
  const vec3 BR = vec3(5.8e-6, 13.5e-6, 33.1e-6);
  const float BM = 16e-6;
  const vec3 BO = vec3(0.650e-6, 1.881e-6, 0.085e-6);
  const float HR = 8000.0, HM = 1200.0;
  muV = max(muV, 0.03);
  float lit = smoothstep(-0.02, 0.06, muS);
  float m = 1.0 / muV + 1.0 / max(muS, 0.03);
  vec3 sumR = vec3(0.0), sumM = vec3(0.0);
  for (int i = 0; i < 8; i++) {
    float u = (float(i) + 0.5) / 8.0;         // e^(-h / HR), from the top down
    float h = -HR * log(u);
    float mie = pow(u, HR / HM);
    float oz = h < 10000.0 ? 15000.0 : h < 25000.0 ? 15000.0 - (h - 10000.0) * (h - 10000.0) / 30000.0
             : h < 40000.0 ? (40000.0 - h) * (40000.0 - h) / 30000.0 : 0.0;
    vec3 tau = BR * HR * u + vec3(BM * 1.1 * HM * mie) + BO * oz;
    vec3 att = exp(-tau * m);
    sumR += att;
    sumM += att * mie / u;
  }
  sumR *= HR / 8.0 / muV;
  sumM *= HR / 8.0 / muV;
  float phaseR = 3.0 / (16.0 * 3.14159265) * (1.0 + cosTheta * cosTheta);
  const float G = 0.7;
  float phaseM = 3.0 / (8.0 * 3.14159265) * ((1.0 - G * G) * (1.0 + cosTheta * cosTheta))
               / ((2.0 + G * G) * pow(1.0 + G * G - 2.0 * G * cosTheta, 1.5));
  vec3 colour = 10.0 * lit * (sumR * BR * (phaseR + 0.05) + sumM * BM * phaseM);
  vec3 trans = exp(-(BR * HR + vec3(BM * 1.1 * HM) + BO * 15000.0) / muV);
  float alpha = 1.0 - dot(trans, vec3(0.3333));
  float interior = smoothstep(0.0, 40000.0, 6371000.0 * (1.0 - sqrt(max(1.0 - muV * muV, 0.0))));
  float k = mix(1.0, 0.55, smoothstep(60000.0, 110000.0, camAlt) * interior);
  return vec4(colour * k, alpha * k);
}
// Mown grass inside the launch complex: the mean of the photo's grass between
// 300 and 450 m from the pad, where the real compound is mown.
const vec3 COMPOUND_GRASS = vec3(0.205, 0.248, 0.118);
float coastBoxW(vec2 uv, float b) { vec2 m = min(uv, 1.0 - uv); return smoothstep(0.0, b, min(m.x, m.y)); }
// Square-root distance code -> metres (see scripts/fetch-ksc-imagery.mjs).
vec2 coastDecode(vec2 c, float range) { vec2 s = (c * 255.0 - 128.0) / 127.0; return sign(s) * s * s * range; }
`;

// Runs right after map_fragment. Declares the c* values the later chunks use.
const EARTH_FRAG_MAIN = /* glsl */`
// The app's day map here, as the globe reads it, for the far sea's flat rule.
vec3 cDay = diffuseColor.rgb;
vec2 cFp = max(abs(dFdx(vLocalPos.xz)) + abs(dFdy(vLocalPos.xz)), vec2(1e-3));
float cFoot = max(cFp.x, cFp.y);          // ground metres per pixel, worst axis
float cView = length(vViewPosition);
float cPad = length(vLocalPos.xz);
vec2 uvN = vGeo * uXfNear.xy + uXfNear.zw;
vec2 uvF = vGeo * uXfFar.xy + uXfFar.zw;
vec2 uvR = vGeo * uXfRegion.xy + uXfRegion.zw;
float wN = coastBoxW(uvN, uCoastBorder.x);
float wF = coastBoxW(uvF, uCoastBorder.y);
float wR = coastBoxW(uvR, uCoastBorder.z);
// The regional 500m fallback must not cover streamed Landsat detail.
// Keep the two finer Cape NAIP layers on top.
vec3 cBase = diffuseColor.rgb;
cBase = mix(cBase, texture2D(uRegionImg, uvR).rgb, wR);
vec4 cSatellite = earthImagerySample((vGeo + uImageryOrigin) / vec2(360.0, 180.0) + 0.5, cBase);
cBase = cSatellite.rgb;
// Aerial photography and Landsat are different surveys. Once pixels cover
// tens of metres, retaining both prints the aerial survey's rectangle and
// offset shorelines over the global image. Keep aerial detail on approach,
// and keep it as a fallback while global tiles are still arriving.
float cAerial = 1.0 - smoothstep(20.0, 60.0, cFoot) * cSatellite.a;
cBase = mix(cBase, texture2D(uCoastFarImg, uvF).rgb, wF * uCoastFarImgOn * cAerial);
cBase = mix(cBase, texture2D(uCoastNearImg, uvN).rgb, wN * uCoastNearImgOn * cAerial);
// Signed distances (m, land positive): x to any water, y to the open sea.
vec2 cD = vec2(4000.0);
#ifndef RK_FAR_PATCH
// Outside the coast masks' boxes the Space Center's patch still reaches open
// ocean: its outer ring runs about 60 km past the region box's 72 W edge.
// Taken as land there, that strip drew the sea matte and unlit beyond a
// straight meridian, and from 60 to 160 km it never crossed to the globe's
// sea. The global water mask (earth_ocean.webp, the one the globe and the far
// sea read) says where the water is wherever no mask does.
if (uEarthOceanOn > 0.5) {
  float cGlobalWater = texture2D(uEarthOceanMask, (vGeo + uImageryOrigin) / vec2(360.0, 180.0) + 0.5).r;
  cD = vec2((0.5 - cGlobalWater) * 8000.0);
}
#endif
vec2 cDNone = cD;
cD = mix(cD, coastDecode(texture(uCoastMasks, vec3(uvR.x, 1.0 - uvR.y, 2.0)).rg, uCoastRange.z), wR);
cD = mix(cD, coastDecode(texture(uCoastMasks, vec3(uvF.x, 1.0 - uvF.y, 1.0)).rg, uCoastRange.y), wF);
cD = mix(cD, coastDecode(texture(uCoastMasks, vec3(uvN.x, 1.0 - uvN.y, 0.0)).rg, uCoastRange.x), wN);
cD = mix(cDNone, cD, uCoastOn);
float cAA = 0.6 * cFoot + 0.25;
float cWater = 1.0 - smoothstep(-cAA, cAA, cD.x);
float cSea = 1.0 - smoothstep(-40.0, 40.0, cD.y);
// Inland water keeps the resolved photograph at altitude. The distance mask
// is for local water geometry/shading; its lower-resolution contour must not
// draw another lake on top of a sharper, independently surveyed image.
float cLocalWater = 1.0 - smoothstep(15.0, 60.0, cFoot);
cWater *= mix(cSea, 1.0, cLocalWater);
float cT = uWaterTime;
// How much of the water here the near-ground sea layer (sea.js) has taken
// over. Zero above its altitude band, where this shader is the water.
float cSeaOwn = seaCoverAt(vLocalPos.xz);
// On the water side it only owns what its own surface can reach: ground the
// mesh draws above sea level hides that surface, so the water there keeps
// its slope, its surf and its colour from this shader.
float cSeaWet = cSeaOwn * (1.0 - smoothstep(0.02, 0.30, seaAboveLevel(vLocalPos)));
// The swash line breathes with the waves along the Atlantic beach; once the
// sea layer is up, the same bores that break out there drive it instead,
// running up the sand and draining back on the shore train's clock.
float cSwash = 2.5 * sin(cT * 0.55 + dot(vLocalPos.xz, vec2(0.021, 0.013))) + 1.2 * sin(cT * 0.9 + vLocalPos.x * 0.047);
// Inland reach of the waterline, in metres of beach.
float cRun = mix(cSwash * 0.6, seaSwashRun(vLocalPos.xz, 0.055), cSeaOwn);

// ---- Land ----
vec3 cLand = cBase;
float cRough = 0.95;
// Inside the launch complex the photo shows the real pad's structures lying
// flat; mown grass replaces it there and the site's own geometry sits on top.
// The override is an octagon just inside the real perimeter road, which the
// photo then draws as the compound's edge.
vec2 cAbs = abs(vLocalPos.xz);
float cOct = max(max(cAbs.x, cAbs.y), (cAbs.x + cAbs.y) * 0.7071);
float cComp = uSite * (1.0 - smoothstep(415.0, 440.0, cOct));
// Mown lawn is never one tone: wetter and drier patches at 20 to 90 m.
vec3 cLawn = COMPOUND_GRASS * (0.74 + 0.32 * tNoise(vLocalPos.xz * 0.011 + 3.7) + 0.16 * tNoise(vLocalPos.xz * 0.045 + 9.1));
cLawn = mix(cLawn, cLawn * vec3(1.12, 1.0, 0.72), smoothstep(0.55, 0.8, tNoise(vLocalPos.xz * 0.006 + 1.9)));
cLand = mix(cLand, cLawn, cComp);
float cBeach = (1.0 - smoothstep(36.0, 68.0, cD.y)) * step(0.0, cD.x);
// Once a pixel covers more ground than the beach is wide, the band lands on
// a scatter of pixels along the shore: a dotted outline round every coast
// seen from altitude. The imagery carries the sand at that scale.
cBeach *= 1.0 - smoothstep(40.0, 120.0, sqrt(cFp.x * cFp.y));
float cWet = 1.0 - smoothstep(0.0, 9.0, cD.y - cRun);
vec3 cSand = mix(SAND_DRY, SAND_WET, cWet);
// The sheet the uprush pushes over the sand: a centimetre of water, so it
// is the glossiest thing on the beach, with a lace of foam at its lip.
float cSheet = cSeaOwn * cBeach * step(0.0, cD.x)
  * (1.0 - smoothstep(cRun - 1.2, cRun + 0.4, cD.y)) * smoothstep(-1.0, 0.5, cD.y);
#ifdef TERRAIN_DETAIL
{
  // Detail textures carry the photo's colour up close: each layer is divided
  // by its own mean (its smallest mip), so it adds grain without shifting hue.
  float nearW = 1.0 - smoothstep(200.0, 2600.0, cView);
  vec2 duv = vLocalPos.xz * uDetailScale;
  vec2 duv2 = mat2(-0.6663, 0.7457, -0.7457, -0.6663) * duv * 0.71 + 0.37;
  float pick = smoothstep(0.4, 0.6, tNoise(vLocalPos.xz * 0.02 + 5.5));
  vec3 dA = mix(texture2D(uDetailA, duv).rgb, texture2D(uDetailA, duv2).rgb, pick);
  vec3 dB = mix(texture2D(uDetailB, duv * 1.37 + 0.21).rgb, texture2D(uDetailB, duv2 * 1.37).rgb, pick);
  vec3 dC = mix(texture2D(uDetailC, duv * 0.83 + 0.63).rgb, texture2D(uDetailC, duv2 * 0.83).rgb, pick);
  vec3 dD = mix(texture2D(uDetailD, duv * 0.5 + 0.11).rgb, texture2D(uDetailD, duv2 * 0.5).rgb, pick);
  vec3 mA = max(textureLod(uDetailA, vec2(0.5), 12.0).rgb, vec3(0.02));
  vec3 mB = max(textureLod(uDetailB, vec2(0.5), 12.0).rgb, vec3(0.02));
  vec3 mC = max(textureLod(uDetailC, vec2(0.5), 12.0).rgb, vec3(0.02));
  vec3 mD = max(textureLod(uDetailD, vec2(0.5), 12.0).rgb, vec3(0.02));
  // Which layer: from what the photo shows and how close the water is.
  float lum = dot(cBase, vec3(0.2126, 0.7152, 0.0722));
  float green = cBase.g - max(cBase.r, cBase.b);
  float wSand = smoothstep(0.14, 0.28, lum) * (1.0 - smoothstep(0.0, 0.025, green));
  float wMarsh = (1.0 - smoothstep(6.0, 55.0, cD.x)) * (1.0 - cSea) * (1.0 - cComp);
  float wGrass = max(smoothstep(0.07, 0.13, lum) * (1.0 - wSand), cComp);
  vec3 ratio = mix(mix(mix(dA / mA, dB / mB, wGrass), dD / mD, wMarsh), dC / mC, max(wSand, cBeach));
  ratio = clamp(ratio, vec3(0.25), vec3(2.2));
  float dStr = nearW * mix(0.8, 0.95, cComp);
  cLand *= mix(vec3(1.0), ratio, dStr);
  cSand *= mix(vec3(1.0), clamp(dC / mC, vec3(0.4), vec3(1.8)), nearW);
}
#endif
cLand = mix(cLand, cSand, cBeach);
cRough = mix(cRough, mix(0.9, 0.38, cWet), cBeach);
// Under the running sheet the sand is a few centimetres down and the
// surface is water: darker, near-mirror, with froth at the lip.
float cLip = cSheet * smoothstep(cRun - 1.2, cRun - 0.2, cD.y)
  * smoothstep(0.35, 0.75, wNoise(vLocalPos.xz * 1.6 + vec2(cT * 0.9, -cT * 0.5)));
cLand = mix(cLand, cSand * 0.62, cSheet);
cLand = mix(cLand, FOAM_COL * 0.85, cLip);
cRough = mix(cRough, 0.12, cSheet);

// ---- Water ----
float cOff = max(0.0, -cD.x);             // metres out from the shore
vec3 cOcean = wMix(OCEAN_SURF, OCEAN_SHELF, smoothstep(15.0, 500.0, cOff));
cOcean = wMix(cOcean, OCEAN_DEEP, smoothstep(800.0, 9000.0, cOff));
// Lagoons keep the photo's own tint: brown-green over seagrass and mud.
vec3 cLagoon = mix(LAGOON_MURK, cBase * 0.85, 0.55);
vec3 cWaterCol = mix(cLagoon, cOcean, cSea);
float cVar;
vec2 cSlope = waveSlope(vLocalPos.xz, cFoot, 1.0 - cSea, cVar);
// Wind is never uniform over a sea: gusty streaks stretched along it and calm
// slicks change the slope variance by tens of percent at 2 to 30 km, which
// is what gives a glint seen from altitude its streaks and bright patches.
vec2 cWindP = mat2(0.96, 0.28, -0.28, 0.96) * vLocalPos.xz * vec2(0.00012, 0.00045);
float cGust = wNoise(cWindP) * 0.6 + wNoise(cWindP * 3.7 + 11.3) * 0.4;
cVar *= mix(1.0, 0.45 + 1.1 * cGust, cSea);
// Surf: spilling breakers, each a sharp white front with a trail of wash
// behind it, walking shoreward on a wobbling line and broken into patches
// along the shore. Once a pixel spans a good part of the spacing between
// them, the pattern gives way to its average, a pale surf strip.
float cPh = cOff / 17.0 + cT * 0.1 + 0.9 * wNoise(vLocalPos.xz * 0.011);
float cFr = fract(cPh);
// A thin bright front, then lace: the wash left behind breaks into cells
// of foam around dark water, thinning as it trails.
vec2 cFp2 = vLocalPos.xz + vec2(cT * 0.8, cT * 0.5);
float cLace = wNoise(cFp2 * 0.22) * 0.55 + wNoise(cFp2 * 0.61 + 4.1) * 0.3 + wNoise(cFp2 * 1.7 + 9.3) * 0.15;
float cFront = smoothstep(0.0, 0.035, cFr) * (1.0 - smoothstep(0.035, 0.11, cFr));
float cWash = (1.0 - smoothstep(0.05, 0.55, cFr)) * smoothstep(0.42 + 0.35 * cFr, 0.62 + 0.3 * cFr, cLace);
float cLines = max(cFront * smoothstep(0.25, 0.55, cLace + 0.2), cWash * 0.8);
cLines *= smoothstep(0.2, 0.65, wNoise(vLocalPos.xz * vec2(0.028, 0.045) + vec2(cT * 0.04, -cT * 0.03)));
cLines = mix(cLines, 0.14, clamp(fwidth(cPh) * 4.0 - 0.3, 0.0, 1.0));
float cFoam = cSea * (1.0 - smoothstep(18.0, 80.0, cOff)) * cLines;
// The swash edge: a thin sheet of foam at the waterline.
cFoam = max(cFoam, cSea * (1.0 - smoothstep(0.0, 2.5 + cFoot, cOff + cRun)) * 0.75);
// Surf is a few tens of metres wide: once a pixel spans more than that it
// is a fraction of the pixel, and from orbit it would ring every coast.
cFoam *= 1.0 - smoothstep(10.0, 60.0, cFoot);
// Where the sea layer owns this pixel it draws the surf and its own
// surface, and the water here becomes the bed beneath it: sand a metre or
// two down on the Atlantic side, the photo's own seagrass in a lagoon. Deep
// water keeps its colour, which is what the two cross-fade through.
float cBedLit = (1.0 - smoothstep(12.0, 130.0, cOff)) * cSea + (1.0 - smoothstep(4.0, 45.0, cOff)) * (1.0 - cSea);
float cBed = cSeaWet * clamp(cBedLit, 0.0, 1.0);
// The bed is still under water, however thin, so it goes through the same
// Beer-Lambert the sea layer uses (seabedDrop's profile, its extinction): a
// strip the sea's own surface cannot reach, behind a ring too coarse to draw
// the shoreline, then still reads as shallow water over sand.
float cDepth = mix(min(0.14 * pow(cOff, 0.55), 2.2), 0.11 * pow(max(-cD.y, 0.0), 0.6666667), cSea);
vec3 cTr = exp(-mix(vec3(2.2, 0.9, 0.8), vec3(0.55, 0.20, 0.26), cSea) * cDepth);
cWaterCol = mix(cWaterCol, mix(cBase * 0.9, SAND_DRY * 0.78, cSea), cBed * cTr);
cFoam *= 1.0 - cSeaWet;
cSlope *= 1.0 - cSeaWet;
cVar = mix(cVar, 0.0025, cSeaWet);
// Micro-ripples under the smallest modelled wave, plus the sun's own width.
float cAlpha = sqrt(0.0036 + 2.0 * (cVar + mix(0.0006, 0.0015, cSea)));
float cWRough = mix(sqrt(cAlpha), 0.55, cBed);

diffuseColor.rgb = mix(cLand, mix(cWaterCol, FOAM_COL, cFoam), cWater);
diffuseColor.a *= vRim;
`;

// The Cape's fragment body, rewritten for a roaming patch. Ground positions
// move to the patch's carried texture frame (so nothing slides when the patch
// recentres), the coastline comes from the day map instead of the Cape's
// masks, and the detail block becomes the land cover system in earthfar.js.
// The Cape's own material never runs any of this: its shader source, and so
// its pixels, are unchanged.
function swapOnce(src, find, repl, what) {
  const i = src.indexOf(find);
  if (i < 0) { console.error('[rocketry] far terrain shader: no ' + what + ' to replace'); return src; }
  return src.slice(0, i) + repl + src.slice(i + find.length);
}

function farFragMain() {
  let src = EARTH_FRAG_MAIN.replace(/vLocalPos\.xz/g, 'cTP').replace(/vLocalPos\.x\b/g, 'cTP.x');
  src = swapOnce(src, 'vec2 cD = vec2(4000.0);', FAR_COAST + '\nvec2 cD = vec2(cFarD);', 'coast default');
  const detail = src.match(/#ifdef TERRAIN_DETAIL[\s\S]*?#endif/);
  if (!detail) console.error('[rocketry] far terrain shader: no detail block to replace');
  else src = swapOnce(src, detail[0], '#ifdef TERRAIN_DETAIL\n' + FAR_LAND + '\n#endif', 'detail block');
  return FAR_FRAME + src;
}

// Land relief shades from the same slopes the patterns draw. Anchored on the
// emissive chunk, which sits after the normal maps and before the lighting.
const FAR_NORMAL = /* glsl */`#include <emissivemap_fragment>
{
  vec2 fnS = cLSlope * (1.0 - cWater);
  normal = normalize(normal - fnS.x * vWaveX - fnS.y * vWaveZ);
}`;

function makeEarthMaterial(texture, { site, detail, transparent, lat0Deg, lon0Deg, far = false }) {
  const m = new THREE.MeshStandardMaterial({
    map: texture || null,
    color: texture ? 0xffffff : 0x5c6a42,
    roughness: 0.95,
    metalness: 0.0,
    transparent,
    depthWrite: true,
    side: THREE.FrontSide
  });
  const L = coastLevels();
  const xf = (lv) => new THREE.Vector4(
    1 / (lv.lonMax - lv.lonMin), 1 / (lv.latMax - lv.latMin),
    (lon0Deg - lv.lonMin) / (lv.lonMax - lv.lonMin), (lat0Deg - lv.latMin) / (lv.latMax - lv.latMin));
  const uniforms = {
    ...coastUniforms,
    ...waterUniforms,
    ...earthViewUniforms,
    ...seaUniforms,
    ...earthImageryUniforms,
    uImageryOrigin: { value: new THREE.Vector2(lon0Deg, lat0Deg) },
    uXfNear: { value: xf(L[0]) },
    uXfFar: { value: xf(L[1]) },
    uXfRegion: { value: xf(L[2]) },
    uRegionImg: { value: regionTexture() },
    uSite: { value: site ? 1 : 0 },
    // Debug view, set from the console or a capture's --eval through
    // patch.materials[i].userData.uniforms.uDebug: 1 water (red) / open sea
    // (green) / land (blue), 2 roughness, 3 specular light only.
    uDebug: { value: 0 },
    uDetailA: { value: detail ? getTexture('florida_scrub') : null },
    uDetailB: { value: detail ? getTexture('florida_grass') : null },
    uDetailC: { value: detail ? getTexture('sand_coast') : null },
    // Away from the Cape the fourth layer is gravel for bare ground, where
    // the Cape uses marsh for its lagoon shores.
    uDetailD: { value: detail ? getTexture(far ? 'moon_regolith' : 'marsh') : null },
    uDetailScale: { value: 1 / 24 },
    ...(far ? {
      uFarM: { value: new THREE.Vector4(1, 0, 0, 1) },
      uFarO: { value: new THREE.Vector2() },
      uFarOB: { value: new THREE.Vector2() },
      uFarTexI: { value: new THREE.Vector2() },
      uFarTexF: { value: new THREE.Vector2() },
      uFarTexScale: { value: new THREE.Vector2(1, 1) },
      uFarTexSize: { value: new THREE.Vector2(1, 1) },
      uFarLat0: { value: lat0Deg },
      uFarCoastQ: { value: [0, 0, 0, 0, 0].map(() => new THREE.Vector2()) },
      uFarWind: { value: new THREE.Vector2(0.87, 0.49) }
    } : {})
  };
  m.userData.uniforms = uniforms;
  m.onBeforeCompile = (shader) => {
    // AERIAL: the dome-matched haze toward the horizon (aerial.js), through
    // the fog chunk's RK_AERIAL branch (environment.js).
    Object.assign(shader.uniforms, uniforms, AERIAL);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + EARTH_VERT_PARS + (far ? '\nuniform vec4 uFarM;' : ''))
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n' + (far
        // Wave and relief slopes on a roaming patch are measured in the
        // texture frame, so the axes they lean against are that frame's.
        ? EARTH_VERT_MAIN.replace('vec3(1.0, 0.0, 0.0)', 'vec3(uFarM.x, 0.0, uFarM.z)').replace('vec3(0.0, 0.0, 1.0)', 'vec3(uFarM.y, 0.0, uFarM.w)')
        : EARTH_VERT_MAIN));
    shader.fragmentShader = '#define RK_AERIAL\n' + (far ? '#define RK_FAR_PATCH\n' : '') + (detail ? '#define TERRAIN_DETAIL\n' : '') + shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + EARTH_FRAG_PARS + (far ? FAR_PARS : ''))
      .replace('#include <map_fragment>', '#include <map_fragment>\n' + (far ? farFragMain() : EARTH_FRAG_MAIN))
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = mix(cRough, mix(cWRough, 0.75, cFoam), cWater);')
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        {
          vec2 s = cSlope * cWater * (1.0 - 0.7 * cFoam);
          normal = normalize(normal - s.x * vWaveX - s.y * vWaveZ);
        }`)
      .replace('#include <lights_physical_fragment>', '#include <lights_physical_fragment>\nmaterial.specularColor = mix(material.specularColor, vec3(0.02), cWater);')
      .replace('#include <opaque_fragment>', `#include <opaque_fragment>
        {
          // Aerial perspective from above the air. The whole sunlit column
          // between the camera and the ground scatters light back, which is
          // what makes the Earth read milky from a spacecraft instead of
          // like geography in a vacuum; without it the patch sat inside the
          // app's planet as a dark disc with a seam at its rim. Inside the
          // air the scene's linear fog carries the haze, so this only rises
          // as the camera leaves it, and it is the same veil, by the same
          // air-mass law, that the app's own Earth shader draws.
          float vHigh = smoothstep(18000.0, 90000.0, uCamAlt);
          if (vHigh > 0.0) {
            float vZen = max(dot(geometryNormal, normalize(vViewPosition)), 0.0);
            float vPath = clamp(0.37 + (1.0 / max(vZen + 0.04, 0.04) - 1.0) * 0.34, 0.0, 1.0);
            float vLit = 0.0;
            #if NUM_DIR_LIGHTS > 0
              vLit = max(dot(geometryNormal, directionalLights[0].direction), 0.0);
            #endif
            float vDay = smoothstep(-0.10, 0.25, vLit);
            vec3 vSky = vec3(0.50, 0.66, 0.86) * (0.55 + 0.45 * vLit);
            gl_FragColor.rgb = wMix(gl_FragColor.rgb, vSky, vHigh * vPath * vDay * 0.50);
            // The sea from up there is the globe's own sea: the same water
            // column, painted water, Cox-Munk glint, daylight, veil and limb
            // light the app's Earth draws (earthocean.js, earth.js), with the
            // sky dome's haze over it as the dome lays it over that Earth
            // (rkDomeGroundHaze), taken over from the patch's near-field water
            // between 60 and 160 km, which is what makes the patch's rim
            // disappear over open water. Only the open
            // sea joins it: applied to every water mask it would paint inland
            // lakes and expose the rectangle of the detailed coast mask.
            float vSeaW = smoothstep(60000.0, 160000.0, uCamAlt) * cWater * cSea;
            if (vSeaW > 0.0 && uEarthOceanOn > 0.5) {
              vec2 gUV = (vGeo + uImageryOrigin) / vec2(360.0, 180.0) + 0.5;
              vec3 gV = normalize(vViewPosition);
              // At night the key light swings to the Moon (environment.js), a
              // cool light where the Sun's is warm; the globe's sea is then
              // on its night side, so the Sun counts as below the horizon.
              float gMuS = -0.2;
              #if NUM_DIR_LIGHTS > 0
                gMuS = mix(-0.2, dot(geometryNormal, directionalLights[0].direction),
                  smoothstep(0.0, 0.08, directionalLights[0].color.r - directionalLights[0].color.b));
              #endif
              float gMuV = clamp(vZen, 0.05, 1.0);
              EarthOcean gSea = earthOceanDecode(texture2D(uEarthOceanMask, gUV).rgb);
              vec3 gColumn = earthWaterReflectance(gSea.depth, gSea.kd490, abs(gUV.y - 0.5) * 180.0, clamp(gMuS, 0.05, 1.0), gMuV);
              vec2 gTexels = gUV * vec2(8192.0, 4096.0);
              float gLod = max(log2(max(length(dFdx(gTexels)), length(dFdy(gTexels)))), 0.0);
              float gOpen = smoothstep(0.9, 1.0, textureLod(uEarthOceanMask, gUV, gLod + 1.5).r);
              vec3 gAlb = mix(gColumn, earthPaintedWater(cDay, gMuV), (1.0 - earthOceanFlat(cDay)) * gOpen) * EARTH_OCEAN_ALBEDO;
              vec3 gLit = gAlb * (earthSkyTint(gMuS) * 0.25 + max(gMuS, 0.0) * 0.75) * EARTH_LIGHT;
              #if NUM_DIR_LIGHTS > 0
                gLit += earthGlintTint(gMuV) * earthGlint(geometryNormal, directionalLights[0].direction, gV, earthOceanWind(gUV, cFoot)) * EARTH_OCEAN_ALBEDO * 0.75 * EARTH_LIGHT;
              #endif
              gLit *= earthSunThroughAir(gMuS) * 0.98;
              gLit = wMix(gLit, vec3(0.50, 0.66, 0.86) * (0.55 + 0.45 * max(gMuS, 0.0)), vPath * smoothstep(-0.10, 0.25, gMuS) * 0.50);
              float gDay = smoothstep(-0.10, 0.22, gMuS);
              gLit *= gDay;
              gLit = mix(gLit, vec3(dot(gLit, vec3(0.2126, 0.7152, 0.0722))), 0.025);
              gLit += vec3(0.62, 0.78, 0.95) * pow(1.0 - vZen, 2.5) * EARTH_LIMB_HAZE * gDay;
              #if NUM_DIR_LIGHTS > 0
                vec4 gDome = rkDomeGroundHaze(vZen, gMuS, dot(-gV, directionalLights[0].direction), uCamAlt);
                gLit = gLit * (1.0 - gDome.a) + gDome.rgb;
              #endif
              gl_FragColor.rgb = mix(gl_FragColor.rgb, gLit, vSeaW);
            }
          }
        }
        if (uDebug > 0.5) {
          vec3 dbg = uDebug < 1.5 ? vec3(cWater, cSea * cWater, 1.0 - cWater)
            : uDebug < 2.5 ? vec3(material.roughness)
            : reflectedLight.directSpecular + reflectedLight.indirectSpecular;
          gl_FragColor = vec4(dbg, 1.0);
        }`);
    if (far) {
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <map_pars_fragment>', '#include <map_pars_fragment>\n' + FAR_MAP_PARS)
        .replace('#include <emissivemap_fragment>', FAR_NORMAL)
        // 4 is the land cover (forest red, open green, sand and ice blue),
        // 5 the distance to the coastline in kilometres either side of grey.
        .replace('if (uDebug > 0.5) {', `if (uDebug > 3.5) {
          gl_FragColor = vec4(uDebug < 4.5 ? cFarDbg : vec3(0.5 + cFarD * 0.00002), 1.0);
        } else if (uDebug > 0.5) {`);
    }
  };
  m.customProgramCacheKey = () => 'rocketry-earth-' + (site ? 's' : 'n') + (transparent ? 't' : 'o') + (detail ? 'd' : 'p') + (far ? 'f' : '');
  return m;
}

// --- Moon and Mars material --------------------------------------------------------

// Regolith does not scatter like a Lambertian surface. Its grains shadow and
// light each other, so reflected radiance follows the Lommel-Seeliger law,
// mu0 / (mu0 + mu), which keeps a low-sun plain bright seen at a low angle
// (under Lambert it goes near black), and it brightens sharply toward zero
// phase angle, where each grain hides its own shadow: the opposition surge
// that makes the ground glow around the shadow of your own head in the
// Apollo photographs. Written relative to Lambert, normalised to match it
// with both sun and eye overhead.
const REGOLITH_DIRECT = /* glsl */`
	{
		float rgMu = saturate( dot( geometryNormal, geometryViewDir ) );
		float rgLS = min( 2.0 / max( dotNL + rgMu, 1e-3 ), 8.0 );
		float rgPhase = acos( clamp( dot( directLight.direction, geometryViewDir ), -1.0, 1.0 ) );
		float rgOpp = 1.0 + 0.8 * exp( - rgPhase / 0.08 );
		reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor ) * rgLS * rgOpp;
	}
`;
const LAMBERT_DIRECT = 'reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseColor );';

// Craters the mesh is too coarse to draw, as shading. The terrain rings carry
// real crater geometry down to about two and a half cells, which from orbit
// leaves the plain between the big craters as smooth as a snowfield. Here a
// 2D field of bowls with raised rims, on a hashed lattice in the body's fixed
// planar frame (so it holds still as the patch recentres), bends the normal
// at four scales from 35 m to 1.3 km craters. Each scale fades in where the
// mesh stops drawing its size and out where it drops below a pixel.
const CRATER_NORMAL_GLSL = /* glsl */`
float rgHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
// Height gradient (m/m, x east, y south) of one crater class with lattice
// spacing cell (m) at planar position p.
vec2 rgCraterGrad(vec2 p, float cell, float seed, float density) {
  vec2 g = vec2(0.0);
  vec2 ip = floor(p / cell);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 c = ip + vec2(float(i), float(j)) + seed * 17.0;
      if (rgHash(c) > density) continue;
      vec2 ctr = (c - seed * 17.0 + 0.2 + 0.6 * vec2(rgHash(c + 3.1), rgHash(c + 7.7))) * cell;
      float r = cell * (0.18 + 0.2 * rgHash(c + 11.3));
      vec2 d = p - ctr;
      float dist = length(d);
      float q = dist / r;
      if (q > 1.7 || dist < 1e-3) continue;
      // Bowl rising from -1 at the centre to a rim at +0.2 (units of depth),
      // then falling back to the plain by 1.7 radii. Old craters are softer.
      float age = rgHash(c + 5.5);
      float dhdq = q < 1.0 ? 2.4 * q : -0.2 * 6.0 * ((q - 1.0) / 0.7) * (1.0 - (q - 1.0) / 0.7) / 0.7;
      float depthOverR = 0.38 * (1.0 - 0.6 * age);
      g += (depthOverR * dhdq) * (d / dist);
    }
  }
  return g;
}
vec2 rgCraterField(vec2 p, float vd) {
  vec2 g = vec2(0.0);
  g += rgCraterGrad(p, 2600.0, 1.0, 0.55) * smoothstep(3800.0, 6000.0, vd) * (1.0 - smoothstep(4.0e5, 6.0e5, vd));
  g += rgCraterGrad(p, 800.0, 2.0, 0.6) * smoothstep(3500.0, 5000.0, vd) * (1.0 - smoothstep(1.2e5, 1.8e5, vd));
  g += rgCraterGrad(p, 240.0, 3.0, 0.6) * smoothstep(3500.0, 5000.0, vd) * (1.0 - smoothstep(3.5e4, 5.0e4, vd));
  g += rgCraterGrad(p, 70.0, 4.0, 0.6) * smoothstep(1600.0, 2600.0, vd) * (1.0 - smoothstep(1.0e4, 1.5e4, vd));
  return g;
}
`;

// Per body: the regolith detail texture is lunar grey, so Mars tints it to
// its rust, and each body's globe map is scaled to its own mean albedo.
const REGOLITH_LOOK = {
  moon: { tint: [1, 1, 1], albedo: 0.42 },
  mars: { tint: [1.12, 0.66, 0.44], albedo: 0.8 }
};

function makeRegolithMaterial(texture, { landColor = 0x8c8c88, transparent = false, detail = true, bodyKey = 'moon' } = {}) {
  const look = REGOLITH_LOOK[bodyKey] || REGOLITH_LOOK.moon;
  const m = new THREE.MeshStandardMaterial({
    map: texture || null,
    color: texture ? 0xffffff : landColor,
    roughness: 0.95,
    metalness: 0.0,
    transparent,
    depthWrite: true,
    side: THREE.FrontSide
  });
  // Close range: a tiling regolith layer, de-tiled by a rotated second
  // sample, and a macro tile, cross-faded into the body's own map by distance.
  const layer = detail ? getTexture('moon_regolith') : null;
  const layerN = detail ? getTexture('moon_regolith_n', { colorSpace: THREE.NoColorSpace }) : null;
  const macro = detail ? getTexture('moon_macro') : null;
  m.userData.uniforms = {
    uDetail: { value: detail ? 1 : 0 },
    uDetailA: { value: layer },
    uDetailN: { value: layerN },
    uDetailRelief: { value: 1.0 },
    uMacro: { value: macro },
    uDetailScale: { value: 1 / 14 },
    uMacroScale: { value: 1 / 3200 },
    // Patch centre in a fixed planar frame for the body (m, x east, z
    // south), so the ground textures stay put when the patch recentres.
    uTexOrigin: { value: new THREE.Vector2() },
    uAlbedoScale: { value: look.albedo },
    uDetailTint: { value: new THREE.Vector3(...look.tint) },
    // Crater shading runs on every ring, the outer one included: it is what
    // keeps the plain from reading as snow from orbit.
    uCraters: { value: 1 }
  };
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, m.userData.uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float rim;\nvarying float vRim;\nvarying vec3 vLocalPos;\nvarying vec3 vEastV;\nvarying vec3 vSouthV;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvRim = rim;\nvLocalPos = position;\nvEastV = normalize(normalMatrix * vec3(1.0, 0.0, 0.0));\nvSouthV = normalize(normalMatrix * vec3(0.0, 0.0, 1.0));');
    const lights = THREE.ShaderChunk.lights_physical_pars_fragment;
    if (lights.includes(LAMBERT_DIRECT)) {
      shader.fragmentShader = shader.fragmentShader.replace('#include <lights_physical_pars_fragment>', lights.replace(LAMBERT_DIRECT, REGOLITH_DIRECT));
    }
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vRim;\nvarying vec3 vLocalPos;\nuniform float uDetail;\nuniform sampler2D uDetailA;\nuniform sampler2D uDetailN;\nuniform float uDetailRelief;\nuniform sampler2D uMacro;\nuniform float uDetailScale;\nuniform float uMacroScale;\nuniform vec2 uTexOrigin;\nuniform float uAlbedoScale;\nuniform vec3 uDetailTint;\nuniform float uCraters;\nvarying vec3 vEastV;\nvarying vec3 vSouthV;\n' + DETAIL_GLSL + CRATER_NORMAL_GLSL)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        if (uCraters > 0.5) {
          vec2 cg = rgCraterField(vLocalPos.xz + uTexOrigin, length(vViewPosition));
          normal = normalize(normal - cg.x * vEastV - cg.y * vSouthV);
        }
        // The regolith tile's normal map, sampled like its colour below
        // (same two de-tiling samples), within the range the tile is seen.
        if (uDetail > 0.5) {
          vec2 tp = vLocalPos.xz + uTexOrigin;
          float w = 1.0 - smoothstep(600.0, 2500.0, length(vViewPosition));
          vec2 duv = tp * uDetailScale;
          const float ca = -0.6663, sa = 0.7457;
          vec2 duv2 = vec2(ca * duv.x - sa * duv.y, sa * duv.x + ca * duv.y) * 0.71 + 0.37;
          float pick = smoothstep(0.4, 0.6, tNoise(tp * 0.02 + 5.5));
          vec3 n1 = texture2D(uDetailN, duv).xyz * 2.0 - 1.0;
          vec3 n2 = texture2D(uDetailN, duv2).xyz * 2.0 - 1.0;
          vec2 s1 = -n1.xy / max(n1.z, 0.2);
          vec2 s2 = -n2.xy / max(n2.z, 0.2);
          s2 = vec2(ca * s2.x + sa * s2.y, -sa * s2.x + ca * s2.y);
          vec2 sl = mix(s1, s2, pick) * uDetailRelief * w;
          normal = normalize(normal - sl.x * vEastV - sl.y * vSouthV);
        }`)
      .replace('#include <map_fragment>', `#include <map_fragment>
        {
          vec3 base = diffuseColor.rgb;
          if (uDetail > 0.5) {
            float viewDist = length(vViewPosition);
            vec2 tp = vLocalPos.xz + uTexOrigin;
            vec2 duv = tp * uDetailScale;
            float rot = 2.3;
            vec2 duv2 = vec2(duv.x * cos(rot) - duv.y * sin(rot), duv.x * sin(rot) + duv.y * cos(rot)) * 0.71 + 0.37;
            float pick = smoothstep(0.4, 0.6, tNoise(tp * 0.02 + 5.5));
            vec3 detailCol = mix(texture2D(uDetailA, duv).rgb, texture2D(uDetailA, duv2).rgb, pick);
            detailCol *= 0.8 + 0.45 * dot(texture2D(uDetailA, duv * 0.11 + 0.42).rgb, vec3(0.333));
            detailCol *= uDetailTint;
            vec3 macroCol = texture2D(uMacro, tp * uMacroScale).rgb * uDetailTint;
            float nearW = 1.0 - smoothstep(600.0, 4500.0, viewDist);
            float macroW = 1.0 - smoothstep(6000.0, 45000.0, viewDist);
            base = mix(base, mix(macroCol, detailCol, nearW), max(macroW, nearW));
          }
          // The imagery is an albedo map made to look right on a globe in the
          // app; under a physical reflectance law it reads as snow. Scaled
          // to the Moon's mean albedo, about 0.12.
          diffuseColor.rgb = base * uAlbedoScale;
          diffuseColor.a *= vRim;
        }`);
  };
  m.customProgramCacheKey = () => 'rocketry-regolith-ls4-' + (transparent ? 't' : 'o') + (detail ? 'd' : 'p');
  return m;
}

// Kept for callers that build a single terrain material directly.
export function makeTerrainMaterial(texture, { site = false, transparent = false, moonlike = false, detail = true, landColor, lat0Deg = 0, lon0Deg = 0, bodyKey, far = false } = {}) {
  if (bodyKey === 'moon') return makeMoonMaterial({ transparent, detail, lat0Deg, lon0Deg });
  if (moonlike) return makeRegolithMaterial(texture, { landColor, transparent, detail, bodyKey });
  return makeEarthMaterial(texture, { site, detail, transparent, lat0Deg, lon0Deg, far });
}

// The core ring's half-width is the inner ring's hole: exactly 21 of the
// inner ring's 78.125 m cells either side of the centre, so the two grids
// share every edge vertex. Four core cells per inner cell puts the beach and
// the dunes a kilometre from the pad on a 19.5 m grid.
const CORE_HALF = 21 * (7500 / 96);
// The micro ring (airless bodies) is the core ring's hole in turn: 21 of its
// 19.5 m cells either side of the centre.
const MICRO_HALF = 21 * (2 * CORE_HALF / 168);

// First patch centre per body: the origin of its planar texture frame.
const TEX_REF = new Map();

// How far the roaming patch's texture frame may drift from zero before it is
// folded back. Every far pattern repeats over these, the fine one for ground
// textures and the coarse one for fields and country-scale variation, so a
// fold moves nothing that can be seen.
const FOLD_FINE = 65536, FOLD_COARSE = 2097152;

const _v3a = new THREE.Vector3(), _v3b = new THREE.Vector3(), _v3c = new THREE.Vector3(), _v3d = new THREE.Vector3();
const _m4a = new THREE.Matrix4(), _m4b = new THREE.Matrix4(), _m4c = new THREE.Matrix4(), _m4d = new THREE.Matrix4();
const _qa = new THREE.Quaternion();
const _tp = { x: 0, y: 0 };

export class TerrainPatch {
  constructor(bodyKey, R, latDeg, lonDeg, texture, opts = {}) {
    this.bodyKey = bodyKey;
    this.R = R;
    this.latDeg = latDeg;
    this.lonDeg = lonDeg;
    this.lat = latDeg * Math.PI / 180;
    this.lon = lonDeg * Math.PI / 180;
    this.basis = siteBasis(this.lat, this.lon);
    if (bodyKey === 'earth') setDayTexture(texture);
    this.centreH = terrainHeight(bodyKey, this.lat, this.lon);
    this.group = new THREE.Group();
    this.group.name = 'terrainPatch:' + bodyKey;
    const site = !!opts.site;
    const moonlike = bodyKey !== 'earth';
    // A roaming Earth patch shades the whole planet, so it reads the day map
    // for its coast and its land cover, and it may move to a new centre.
    const far = !moonlike && !!opts.far;
    this.far = far;
    // Rotation and offsets that take a patch-local point to the frame every
    // far pattern is drawn in; carried across recentres so nothing slides.
    this.texFrame = { c: 1, s: 0, ox: 0, oz: 0, bx: 0, bz: 0 };
    const landColor = bodyKey === 'moon' ? 0x8c8c88 : 0x9a6a48;
    const matOpts = { site, landColor, moonlike, lat0Deg: latDeg, lon0Deg: lonDeg, bodyKey, far };
    const near = makeTerrainMaterial(texture, matOpts);
    const mid = makeTerrainMaterial(texture, matOpts);
    const outer = makeTerrainMaterial(texture, { ...matOpts, site: false, transparent: true, detail: false });
    this.materials = [near, mid, outer];
    // Airless bodies get a finer ring under the vessel, 4.9 m cells across
    // 820 m, so the small craters around a lander are there to land among.
    const micro = moonlike ? [{ half: MICRO_HALF, cells: 168, hole: 0, opts: { morph: { half: CORE_HALF, cells: 168 } }, mat: near, order: 3, shadow: true }] : [];
    this.specs = [
      ...micro,
      { half: CORE_HALF, cells: 168, hole: moonlike ? 2 * MICRO_HALF : 0, opts: { morph: { half: 3750, cells: 96 } }, mat: near, order: 2, shadow: true },
      { half: 3750, cells: 96, hole: 2 * CORE_HALF, opts: { morph: { half: 56250, cells: 96 } }, mat: near, order: 1, shadow: true },
      { half: 56250, cells: 96, hole: 7500, opts: { morph: { half: 900000, cells: 128 } }, mat: mid, order: 0, shadow: false },
      { half: 900000, cells: 128, hole: 112500, opts: { rimFade: true }, mat: outer, order: -1, shadow: false }
    ];
    this._updateTexOrigin();
    this._updateFarUniforms();
    if (bodyKey === 'moon') {
      this.hz = createHorizonMaps();
      if (!opts.defer) { const st = horizonMapSteps(this.hz, this.basis, R, dirAt); while (!st.next().done) { /* build now */ } }
      this._bindHorizon(this.hz);
    }
    // A roaming patch builds its rings a few rows a frame (opts.defer), so
    // arriving over a new part of the world costs no dropped frame; the patch
    // is not ready, and so not shown, until they are all in.
    this.ready = !opts.defer;
    this.meshes = this.specs.map((s) => {
      const mesh = new THREE.Mesh(this.ready ? this.buildGeometry(s) : new THREE.BufferGeometry(), s.mat);
      mesh.receiveShadow = s.shadow;
      mesh.castShadow = false;
      mesh.renderOrder = s.order;
      mesh.frustumCulled = false;
      this.group.add(mesh);
      return mesh;
    });
    // The city lights at night (nightimagery.js): each ring drawn again,
    // additively, by a material of its own, since the ground's material has no
    // texture units left for the night pages. Same geometry, drawn after it.
    if (bodyKey === 'earth') {
      this.nightMeshes = this.meshes.map((mesh, i) => {
        const ring = this.specs[i];
        const night = new THREE.Mesh(mesh.geometry, makeNightPatchMaterial(ring.mat.userData.uniforms.uImageryOrigin, { transparent: !!ring.mat.transparent, ground: ring.mat.userData.uniforms }));
        night.renderOrder = ring.order + 0.05;
        night.frustumCulled = false;
        night.name = 'nightLights';
        night.onBeforeRender = nightPatchBeforeRender(this.group);
        mesh.add(night);
        return night;
      });
      this.materials.push(...new Set(this.nightMeshes.map(m => m.material)));
    }
    if (!this.ready) this._startJob(latDeg, lonDeg);
    // Earth's heights depend on the coastline data, which streams in after
    // the first frame; rebuild the rings once it is here.
    if (bodyKey === 'earth') {
      loadCoast();
      if (!coastReady()) this._unsubscribe = onCoastReady(() => this.rebuild());
    }
    // The Moon's heights stream in as LOLA tiles; rebuild (a few rows a
    // frame, like a recentre) when one lands under the patch.
    if (bodyKey === 'moon') {
      loadMoonBase();
      this._unsubscribe = onMoonDem((batch) => {
        if (this.disposed || !batch.some(t => this._touchesTile(t))) return;
        if (this._job) this._demPending = true;
        else this._startJob(this.latDeg, this.lonDeg);
      });
    }
  }

  _bindHorizon(maps) {
    if (this.hz && this.hz !== maps) { this.hz.near.dispose(); this.hz.mid.dispose(); this.hz.far.dispose(); }
    this.hz = maps;
    for (const m of this.materials) {
      const u = m.userData.uniforms;
      if (!u || !u.uHzNear) continue;
      u.uHzNear.value = maps.near; u.uHzMid.value = maps.mid; u.uHzFar.value = maps.far; u.uHzReady.value = maps.ready ? 1 : 0;
    }
  }

  // True if DEM tile { z, x, y } overlaps the patch's outer ring.
  _touchesTile({ z, x, y }) {
    const span = moonSpan(z);
    const reach = 900e3 / this.R * DEG;
    const lon0 = -180 + x * span, lat1 = 90 - y * span;
    const dLat = Math.max(0, lat1 - span - this.latDeg, this.latDeg - lat1);
    const cl = Math.max(0.05, Math.cos(this.lat));
    const mid = lon0 + span / 2;
    const dl = Math.abs(THREE.MathUtils.euclideanModulo(this.lonDeg - mid + 180, 360) - 180);
    const dLon = Math.max(0, dl - span / 2) * cl;
    return Math.hypot(dLat, dLon) < reach;
  }

  buildGeometry(s) {
    return buildRing(this.bodyKey, this.R, this.basis, this.centreH, s.half, s.cells, s.hole, s.opts, this.latDeg, this.lonDeg);
  }

  // Patch centre in the body's fixed planar texture frame (see uTexOrigin).
  _updateTexOrigin() {
    let ref = TEX_REF.get(this.bodyKey);
    if (!ref) { ref = { lat: this.lat, lon: this.lon }; TEX_REF.set(this.bodyKey, ref); }
    const dLon = THREE.MathUtils.euclideanModulo(this.lon - ref.lon + Math.PI, TWO_PI) - Math.PI;
    const east = this.R * Math.cos(ref.lat) * dLon, north = this.R * (this.lat - ref.lat);
    for (const m of this.materials) {
      const imagery = m.userData.uniforms?.uImageryOrigin;
      if (imagery) imagery.value.set(this.lonDeg, this.latDeg);
      if (imagery && m.userData.uniforms.uXfNear) coastLevels().forEach((level, i) => {
        const transform = m.userData.uniforms[['uXfNear', 'uXfFar', 'uXfRegion'][i]].value;
        transform.z = (this.lonDeg - level.lonMin) / (level.lonMax - level.lonMin);
        transform.w = (this.latDeg - level.latMin) / (level.latMax - level.latMin);
      });
      const u = m.userData.uniforms && m.userData.uniforms.uTexOrigin;
      // The Moon's regolith patterns repeat over 108 m; folding keeps their
      // coordinates small (float precision) without moving anything.
      if (u && m.userData.moonFrame) u.value.set(THREE.MathUtils.euclideanModulo(east, 6912), THREE.MathUtils.euclideanModulo(-north, 6912));
      else if (u) u.value.set(east, -north);
      if (m.userData.moonFrame) setMoonPatchFrame(m, this.basis, this.R + this.centreH);
    }
  }

  // The uniforms a roaming patch's shader needs: its texture frame, where its
  // centre sits on the day map, and the coastline noise lattice there.
  _updateFarUniforms() {
    if (!this.far) return;
    const size = dayMapSize();
    const t = this.texFrame;
    const texX = (this.lonDeg + 180) / 360 * size.w - 0.5;
    const texY = (90 - this.latDeg) / 180 * size.h - 0.5;
    const q = coastLatticeOffsets(this.latDeg, this.lonDeg);
    for (const m of this.materials) {
      const u = m.userData.uniforms;
      if (!u || !u.uFarM) continue;
      u.uFarM.value.set(t.c, t.s, -t.s, t.c);
      u.uFarO.value.set(t.ox, t.oz);
      u.uFarOB.value.set(t.bx, t.bz);
      u.uFarTexI.value.set(Math.floor(texX), Math.floor(texY));
      u.uFarTexF.value.set(texX - Math.floor(texX), texY - Math.floor(texY));
      u.uFarTexScale.value.set(size.w / 360, size.h / 180);
      u.uFarTexSize.value.set(size.w, size.h);
      u.uFarLat0.value = this.latDeg;
      for (let k = 0; k < q.length; k++) u.uFarCoastQ.value[k].set(q[k][0], q[k][1]);
    }
  }

  // Where a patch-local point lands in the far patterns' frame.
  texFramePoint(x, z, out) {
    const t = this.texFrame;
    out.x = t.c * x - t.s * z + t.ox;
    out.y = t.s * x + t.c * z + t.oz;
    return out;
  }

  // Carry the texture frame to a new centre: the same ground point keeps the
  // same coordinates, to the second order of the sphere's curvature, so no
  // pattern moves when the rings are swapped. The offsets fold back toward
  // zero over the pattern periods, which keeps float precision near the
  // ground after a long way flown.
  _carryTexFrame(basis, centreH) {
    const t = this.texFrame;
    const cNew = _v3a.copy(basis.up).multiplyScalar(this.R + centreH);
    const cOld = _v3b.copy(this.basis.up).multiplyScalar(this.R + this.centreH);
    const d = cNew.sub(cOld);
    const tx = d.dot(this.basis.east), tz = -d.dot(this.basis.north);
    // The new frame's axes seen from the old one: a rotation by construction.
    const a00 = basis.east.dot(this.basis.east), a10 = -basis.east.dot(this.basis.north);
    const ang = Math.atan2(a10, a00);
    const ca = Math.cos(ang), sa = Math.sin(ang);
    const c = t.c * ca - t.s * sa, s = t.s * ca + t.c * sa;
    const ox = t.c * tx - t.s * tz + t.ox, oz = t.s * tx + t.c * tz + t.oz;
    const bx = t.c * tx - t.s * tz + t.bx, bz = t.s * tx + t.c * tz + t.bz;
    const n = Math.hypot(c, s) || 1;
    t.c = c / n; t.s = s / n;
    t.ox = ox - FOLD_FINE * Math.round(ox / FOLD_FINE);
    t.oz = oz - FOLD_FINE * Math.round(oz / FOLD_FINE);
    t.bx = bx - FOLD_COARSE * Math.round(bx / FOLD_COARSE);
    t.bz = bz - FOLD_COARSE * Math.round(bz / FOLD_COARSE);
  }

  // Move to a new centre without a hitch: the rings are rebuilt a few rows a
  // frame by tick() and swapped in together; the old ones stay until then.
  // Rebuilding every ring at once costs about 170 ms on the Moon.
  recentre(latDeg, lonDeg) {
    if (this._job || this.disposed) return;
    this._startJob(latDeg, lonDeg);
  }

  _startJob(latDeg, lonDeg) {
    const lat = latDeg * Math.PI / 180, lon = lonDeg * Math.PI / 180;
    const basis = siteBasis(lat, lon);
    const centreH = terrainHeight(this.bodyKey, lat, lon);
    const outs = this.specs.map(() => ({}));
    const steps = this.specs.map((s, i) => buildRingSteps(this.bodyKey, this.R, basis, centreH, s.half, s.cells, s.hole, s.opts, latDeg, lonDeg, outs[i]));
    // The Moon's shadow height maps for the new centre, into a fresh pair so
    // the old ones stay bound until the rings swap.
    if (this.hz) { outs.hz = createHorizonMaps(); steps.push(horizonMapSteps(outs.hz, basis, this.R, dirAt)); }
    this._job = { latDeg, lonDeg, lat, lon, basis, centreH, steps, outs, i: 0 };
  }

  get recentring() { return !!this._job; }

  // An Earth patch away from the Cape can move to a new centre; the Space
  // Center's cannot, since its site, smoke and cloud decks are built on it.
  get canRecentre() { return this.far; }

  // Advance a pending recentre for about budgetMs. True on the call that
  // swaps the new rings in.
  tick(budgetMs = 5) {
    const j = this._job;
    if (!j) return false;
    const t0 = performance.now();
    while (j.i < j.steps.length) {
      if (j.steps[j.i].next().done) j.i++;
      if (performance.now() - t0 > budgetMs) return false;
    }
    if (this.ready) {
      this._carryTexFrame(j.basis, j.centreH);
      this._moveChildren(j.basis, j.centreH);
    }
    Object.assign(this, { latDeg: j.latDeg, lonDeg: j.lonDeg, lat: j.lat, lon: j.lon, basis: j.basis, centreH: j.centreH });
    this.meshes.forEach((mesh, i) => {
      const old = mesh.geometry;
      mesh.geometry = j.outs[i].geometry;
      if (this.nightMeshes) this.nightMeshes[i].geometry = mesh.geometry;
      old.dispose();
    });
    if (j.outs.hz) this._bindHorizon(j.outs.hz);
    this._job = null;
    this.ready = true;
    this._updateTexOrigin();
    this._updateFarUniforms();
    if (this._demPending) { this._demPending = false; this._startJob(this.latDeg, this.lonDeg); }
    return true;
  }

  // Anything else riding on the patch (smoke and sparks from a crash) is
  // placed in the patch's frame, so it has to be carried into the new one or
  // it jumps by the distance the centre moved.
  _moveChildren(basis, centreH) {
    const rest = this.group.children.filter(c => !this.meshes.includes(c));
    if (!rest.length) return;
    const cNew = _v3a.copy(basis.up).multiplyScalar(this.R + centreH);
    const cOld = _v3b.copy(this.basis.up).multiplyScalar(this.R + this.centreH);
    _m4a.makeBasis(this.basis.east, this.basis.up, _v3d.copy(this.basis.north).negate());
    _m4b.makeBasis(basis.east, basis.up, _v3d.copy(basis.north).negate());
    // Old local -> surface-fixed -> new local, both bases being rotations.
    const invNew = _m4c.copy(_m4b).transpose();
    const toNew = _m4d.multiplyMatrices(invNew, _m4a);
    const shift = _v3c.copy(cOld).sub(cNew).applyMatrix4(invNew);
    const q = _qa.setFromRotationMatrix(toNew);
    for (const child of rest) {
      child.position.applyMatrix4(toNew).add(shift);
      child.quaternion.premultiply(q);
    }
  }

  rebuild() {
    if (this.disposed) return;
    this.centreH = terrainHeight(this.bodyKey, this.lat, this.lon);
    this.meshes.forEach((mesh, i) => {
      const old = mesh.geometry;
      mesh.geometry = this.buildGeometry(this.specs[i]);
      if (this.nightMeshes) this.nightMeshes[i].geometry = mesh.geometry;
      old.dispose();
    });
  }

  // Local y (m) of the terrain surface at patch-local (x, z), curvature
  // included: 5 km out the ground is already 2 m below the tangent plane.
  groundY(x, z) {
    const dir = dirAt(this.basis, this.R, x, z, _dirG);
    const h = terrainHeight(this.bodyKey, Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1)), Math.atan2(-dir.z, dir.x));
    return (this.R + h) * dir.dot(this.basis.up) - (this.R + this.centreH);
  }

  // Site centre in body surface-fixed coordinates (metres).
  centreSurface() {
    const r = this.R + this.centreH;
    return { x: this.basis.up.x * r, y: this.basis.up.y * r, z: this.basis.up.z * r };
  }

  // Quaternion taking patch-local (east, up, south) to surface-fixed axes.
  surfaceQuaternion(out = new THREE.Quaternion()) {
    const m = new THREE.Matrix4().makeBasis(this.basis.east, this.basis.up, this.basis.north.clone().negate());
    return out.setFromRotationMatrix(m);
  }

  dispose() {
    this.disposed = true;
    if (this._unsubscribe) this._unsubscribe();
    for (const m of this.meshes) m.geometry.dispose();
    for (const m of this.materials) m.dispose();
    if (this.hz) { this.hz.near.dispose(); this.hz.mid.dispose(); this.hz.far.dispose(); }
  }
}

const _camLocal = new THREE.Vector3();
const _dirG = new THREE.Vector3();
// Per frame for the Earth patch: wave phases about the point under the camera.
// A roaming patch measures them in its own texture frame, the one every far
// pattern is drawn in, so the sea holds still as the patch recentres.
export function updateTerrainWater(patch, camera, time) {
  camera.getWorldPosition(_camLocal);
  patch.group.worldToLocal(_camLocal);
  // The camera's height over the sphere, for the orbital haze.
  const rr = patch.R + patch.centreH + _camLocal.y;
  earthViewUniforms.uCamAlt.value = Math.hypot(rr, _camLocal.x, _camLocal.z) - patch.R;
  earthViewUniforms.uEarthOceanOn.value = earthImageryUniforms.uEarthOceanMask?.value?.userData?.earthOcean ? 1 : 0;
  // The lights pass only while some of the patch (900 km, 0.14 rad, from its
  // centre) is past the globe's twilight line, where they begin (Sun 0.22
  // above the horizon in sine).
  if (patch.nightMeshes) {
    const lit = patch.basis.up.dot(nightSunUniforms.uNightSunSurface.value) > Math.sin(Math.asin(0.22) + 0.15);
    for (const m of patch.nightMeshes) m.visible = !lit;
  }
  if (patch.far) {
    patch.texFramePoint(_camLocal.x, _camLocal.z, _tp);
    updateWaves(time, _tp.x, _tp.y);
  } else {
    updateWaves(time, _camLocal.x, _camLocal.z);
  }
}

// Convenience for physics: terrain radius (from the body centre) under a
// surface-fixed position.
export function surfaceRadiusAt(bodyKey, R, surfacePos) {
  const r = Math.hypot(surfacePos.x, surfacePos.y, surfacePos.z);
  const lat = Math.asin(surfacePos.y / r);
  const lon = Math.atan2(-surfacePos.z, surfacePos.x);
  return R + terrainHeight(bodyKey, lat, lon);
}
